import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Step1 from "../../components/newBookingFlow/Step1";
import Step2 from "../../components/newBookingFlow/Step2";
import Step4 from "../../components/newBookingFlow/Step4";
import SuccessScreen from "../../components/newBookingFlow/SuccessScreen";
import GlobalHeader from "../../components/newBookingFlow/GlobalHeader";
import "./BookingFlow.css";
import { Helmet } from "react-helmet";
import { isBookingFeePaid } from "../../utils/bookingFlowPayment";

const BookingFlow = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [cartData, setCartData] = useState(null);
  const [isLoading] = useState(false);
  const hasInitializedStep = useRef(false);

  // Check if we're on a success screen
  const isSuccessScreen =
    searchParams.get("session_id") && searchParams.get("type");

  // Initialize current step from cart data when it loads (only once)
  useEffect(() => {
    console.log("BookingFlow useEffect - cartData changed:", cartData);

    // Fee paid but booking not finished — stay on checkout, don't redirect away
    if (isBookingFeePaid(cartData) && !hasInitializedStep.current) {
      console.log("Booking fee already paid — opening checkout to complete booking");
      setCurrentStep(3);
      hasInitializedStep.current = true;
      return;
    }

    // Fully completed flow (post-booking extras step)
    if (cartData?.status === "completed" && cartData?.currentStep >= 5) {
      console.log("Cart is completed, redirecting to extras page");
      const bookingId = cartData._id;
      console.log("Redirecting to extras page with booking ID:", bookingId);
      window.location.href = `/booking-extras/${bookingId}`;
      return;
    }

    if (cartData?.currentStep && !hasInitializedStep.current) {
      console.log("Loading current step from cart:", cartData.currentStep);

      const normalizedStep =
        cartData.currentStep >= 3 ? 3 : cartData.currentStep;

      if (normalizedStep >= 1 && normalizedStep <= 3) {
        setCurrentStep(normalizedStep);
        hasInitializedStep.current = true;
      } else {
        console.log(
          "Invalid step in cart:",
          cartData.currentStep,
          "- starting fresh"
        );
        setCurrentStep(1);
        hasInitializedStep.current = true;
      }
    } else {
      console.log(
        "No currentStep found in cart or already initialized, keeping current step:",
        currentStep
      );
    }
  }, [cartData, currentStep]);
  // Function to clear cart cookie
  const clearCartCookie = () => {
    document.cookie =
      "ccw_booking_cart_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
  };

  const handleCartUpdate = (updatedCart) => {
    setCartData(updatedCart);
  };

  const handleStepComplete = (
    updatedCart,
    targetStep = null,
    bookingInfo = null
  ) => {
    console.log(
      "BookingFlow handleStepComplete called with cart:",
      updatedCart
    );
    console.log("Current step before progression:", currentStep);
    console.log("Target step:", targetStep);
    console.log("Booking info:", bookingInfo);

    setCartData(updatedCart);

    // Store booking data if provided (not needed for current flow)
    // if (bookingInfo) {
    //   setBookingData(bookingInfo);
    // }

    // Move to target step or next step
    const nextStep = targetStep || currentStep + 1;
    console.log("Moving to step:", nextStep);
    setCurrentStep(nextStep);

    // If booking is confirmed (step 5), redirect to Step 5 page
    if (nextStep === 5 && bookingInfo?._id) {
      clearCartCookie();
      navigate(`/booking-extras/${bookingInfo._id}`);
      return;
    }

    console.log("Step completed, moved to step:", nextStep);
  };

  const handleStepBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  // const handleNavigate = (targetStep) => {
  //   setCurrentStep(targetStep);
  // };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1
            cartData={cartData}
            onStepComplete={handleStepComplete}
            onCartUpdate={handleCartUpdate}
            isLoading={isLoading}
          />
        );
      case 2:
        return (
          <Step2
            cartData={cartData}
            onStepComplete={handleStepComplete}
            onCartUpdate={handleCartUpdate}
            onStepBack={handleStepBack}
            isLoading={isLoading}
          />
        );
      case 3:
        return (
          <Step4
            cartData={cartData}
            onStepComplete={handleStepComplete}
            onGoBack={handleStepBack}
            onCartUpdate={handleCartUpdate}
          />
        );
      default:
        return <div>Invalid step</div>;
    }
  };

  // If we're on a success screen, render it directly
  if (isSuccessScreen) {
    return <SuccessScreen />;
  }

  return (
    <div className="booking-flow-container">
      <Helmet>
        <title>Book Your Car Wash - Concierge Car Wash</title>
        <meta
          name="description"
          content="Book your car wash service online with Concierge Car Wash. Professional car detailing services with flexible scheduling."
        />
      </Helmet>

      {/* Global Header */}
      <GlobalHeader />

      {/* Current Step Content */}
      <div className="step-content">{renderCurrentStep()}</div>
    </div>
  );
};

export default BookingFlow;
